/**
 * Trovva — Web Activity Panel
 *
 * Shows a contact's website activity tracked via the Trovva Pixel.
 * Displays visits, time on page, property interests, and real-time indicators.
 *
 * Inline styles only, using theme from '../../lib/theme'.
 */

import { useState, useEffect } from 'react';
import { Globe, Clock, Eye, Home, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { theme } from '../../lib/theme';
import { supabase } from '../../lib/supabase';
import { formatDate } from '../../lib/dateFormat';

export default function WebActivityPanel({ contactId }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalVisits: 0,
    lastVisited: null,
    lastVisitedDate: null,
    totalPages: 0,
    isActivenow: false,
    propertyPages: [],
  });

  // Load web activity from pixel_visits table
  useEffect(() => {
    (async () => {
      if (!contactId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Not authenticated');

        // Fetch visits for this contact
        const r = await fetch(`/api/web-activity?contact_id=${contactId}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (!r.ok) {
          const data = await r.json();
          throw new Error(data.error || `Failed (${r.status})`);
        }

        const data = await r.json();
        setVisits(data.visits || []);

        // Compute stats
        if (data.visits && data.visits.length > 0) {
          const now = Date.now();
          const lastVisit = new Date(data.visits[0].created_at);
          const lastVisitMs = lastVisit.getTime();
          const isActive = now - lastVisitMs < 15 * 60 * 1000; // 15 minutes

          // Group property page visits
          const propertyPages = {};
          let pageCount = 0;
          data.visits.forEach(v => {
            pageCount++;
            if (v.is_property_page) {
              const url = v.url || 'Unknown Property';
              propertyPages[url] = (propertyPages[url] || 0) + 1;
            }
          });

          setStats({
            totalVisits: data.visits.length,
            lastVisited: formatRelativeTime(lastVisit),
            lastVisitedDate: lastVisit,
            totalPages: pageCount,
            isActivenow: isActive,
            propertyPages: Object.entries(propertyPages)
              .map(([url, count]) => ({ url, count }))
              .sort((a, b) => b.count - a.count)
              .slice(0, 5), // Top 5
          });
        }
      } catch (e) {
        console.error('[WebActivityPanel] error:', e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [contactId]);

  // Format time like "2 hours ago"
  function formatRelativeTime(date) {
    if (!date) return null;
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return formatDate(date);
  }

  // Format duration like "3m 45s"
  function formatDuration(seconds) {
    if (!seconds) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  }

  // ── Styles ────────────────────────────────────────────
  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  };

  const sectionTitleStyle = {
    fontSize: 13,
    fontWeight: 700,
    color: theme.colors.text,
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  };

  const statCardStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: 12,
    background: theme.colors.bg,
    borderRadius: 8,
    border: `1px solid ${theme.colors.border}`,
  };

  const statLabelStyle = {
    fontSize: 11,
    color: theme.colors.textLight,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    fontWeight: 600,
  };

  const statValueStyle = {
    fontSize: 20,
    fontWeight: 700,
    color: theme.colors.primary,
    marginTop: 2,
  };

  const visitListStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    maxHeight: 320,
    overflowY: 'auto',
  };

  const visitItemStyle = {
    padding: 10,
    background: theme.colors.bg,
    borderRadius: 6,
    border: `1px solid ${theme.colors.border}`,
    fontSize: 12,
  };

  const visitTitleStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: theme.colors.text,
    marginBottom: 4,
    wordBreak: 'break-word',
    display: 'flex',
    alignItems: 'start',
    gap: 6,
  };

  const visitMetaStyle = {
    display: 'flex',
    gap: 8,
    fontSize: 11,
    color: theme.colors.textLight,
    flexWrap: 'wrap',
  };

  const badgeStyle = (bg, fg) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    padding: '2px 6px',
    borderRadius: 4,
    background: bg,
    color: fg,
    fontSize: 10,
    fontWeight: 600,
  });

  const emptyStateStyle = {
    padding: 24,
    textAlign: 'center',
    color: theme.colors.textLight,
    fontSize: 13,
  };

  const activeIndicatorStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 8px',
    background: '#ECFDF5',
    color: '#10B981',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
  };

  // ── Render ────────────────────────────────────────────
  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, color: theme.colors.textLight }}>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', marginRight: 8 }} />
          Loading web activity…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: '#FEF2F2', borderRadius: 8, border: `1px solid ${theme.colors.danger}20` }}>
          <AlertCircle size={16} color={theme.colors.danger} />
          <span style={{ fontSize: 12, color: theme.colors.danger }}>{error}</span>
        </div>
      </div>
    );
  }

  if (visits.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={emptyStateStyle}>
          <Globe size={32} style={{ opacity: 0.3, marginBottom: 8, margin: '0 auto 8px' }} />
          <div style={{ fontWeight: 600, marginBottom: 4 }}>No website activity tracked yet</div>
          <div style={{ fontSize: 12, color: theme.colors.textLight }}>
            Add the Trovva Pixel to your website to see visitor activity here.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Summary Stats */}
      <div>
        <div style={sectionTitleStyle}>
          <Eye size={14} /> Overview
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {/* Total Visits */}
          <div style={statCardStyle}>
            <div>
              <div style={statLabelStyle}>Total Visits</div>
              <div style={statValueStyle}>{stats.totalVisits}</div>
            </div>
          </div>

          {/* Last Visited */}
          <div style={statCardStyle}>
            <div>
              <div style={statLabelStyle}>Last Visited</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text, marginTop: 2 }}>
                {stats.lastVisited}
                {stats.isActivenow && (
                  <div style={activeIndicatorStyle}>
                    <span style={{ width: 6, height: 6, background: '#10B981', borderRadius: 3 }} /> Active now
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Property Interest Summary */}
      {stats.propertyPages.length > 0 && (
        <div>
          <div style={sectionTitleStyle}>
            <Home size={14} /> Property Interest
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {stats.propertyPages.map((prop, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 10, background: theme.colors.bg, borderRadius: 6, border: `1px solid ${theme.colors.border}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text, marginBottom: 2, wordBreak: 'break-word' }}>
                    {prop.url.length > 60 ? prop.url.substring(0, 60) + '…' : prop.url}
                  </div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 600, color: theme.colors.primary, marginLeft: 8, whiteSpace: 'nowrap' }}>
                  {prop.count} {prop.count === 1 ? 'visit' : 'visits'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Visits */}
      <div>
        <div style={sectionTitleStyle}>
          <Clock size={14} /> Recent Visits
        </div>
        <div style={visitListStyle}>
          {visits.map((visit, idx) => (
            <div key={idx} style={visitItemStyle}>
              <div style={visitTitleStyle}>
                {visit.is_property_page && (
                  <span style={badgeStyle('#FEF3C7', '#92400E')}>
                    <Home size={10} /> Property
                  </span>
                )}
                <div>
                  <a
                    href={visit.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: theme.colors.primary,
                      textDecoration: 'none',
                      wordBreak: 'break-word',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    {visit.title || 'Untitled'}
                    <ExternalLink size={11} />
                  </a>
                </div>
              </div>

              <div style={visitMetaStyle}>
                <span>{formatRelativeTime(new Date(visit.created_at))}</span>
                {visit.duration_seconds && (
                  <>
                    <span>•</span>
                    <span>{formatDuration(visit.duration_seconds)} on page</span>
                  </>
                )}
              </div>

              {visit.referrer && (
                <div style={{ marginTop: 6, fontSize: 11, color: theme.colors.textLight, wordBreak: 'break-word' }}>
                  From: {visit.referrer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
